import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { noop, snakeCase } from "lodash";

// Components
import Link from "./Link";

// Stylesheets
import "./Button.scss";

const onClickHandler = (onClick, preventDefault, linkProps) => (evt) => {
  // If this is a button link (link styled as a button),
  // then we should not preventDefault as this will prevent
  // it from navigating to the appropriate page
  if (preventDefault && !linkProps) {
    evt.preventDefault();
  }

  onClick(evt);
};

const renderButtonText = (enableTracking, id, children) => {
  const props = {
    className: classNames("button_text", { is_tracked: enableTracking }),
  };

  if (id) {
    props.id = `${id}_button_text`;
  }

  return <span {...props}>{children}</span>;
};

export const Button = (props) => {
  const {
    linkProps,
    className,
    color,
    size,
    buttonType,
    preventDefault,
    onClick,
    enableTracking,
    isDisabled,
    ...rest
  } = props;

  const buttonProps = {
    ...rest,
    onClick: onClickHandler(
      props.onClick,
      props.preventDefault,
      props.linkProps
    ),
    disabled: props.isDisabled,
  };

  const classes = classNames(
    "button",
    `button_${snakeCase(props.size)}`,
    `button_${props.color}`,
    `button_${props.buttonType}`,
    {
      is_disabled: props.isDisabled,
      is_tracked: props.enableTracking,
    },
    props.className
  );

  const buttonChildren = renderButtonText(
    props.enableTracking,
    props.id,
    props.children
  );

  if (props.linkProps) {
    return (
      <Link className={classes} {...props.linkProps} {...buttonProps}>
        {buttonChildren}
      </Link>
    );
  } else {
    return (
      <button className={classes} type="button" {...buttonProps}>
        {buttonChildren}
      </button>
    );
  }
};

Button.propTypes = {
  children: PropTypes.any,
  linkProps: PropTypes.object,
  className: PropTypes.any,
  id: PropTypes.string,
  size: PropTypes.oneOf(["fixed", "stretch", "mobileOnlyStretch"]),
  color: PropTypes.oneOf(["blue", "white", "rose", "teal", "navy"]),
  buttonType: PropTypes.oneOf(["primary", "secondary"]),
  enableTracking: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func,
  preventDefault: PropTypes.bool,
};

Button.defaultProps = {
  isDisabled: false,
  enableTracking: false,
  size: "fixed",
  color: "blue",
  buttonType: "primary",
  preventDefault: true,
  onClick: noop,
};

export default Button;
